import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Star, ShoppingBag, ArrowLeft, Truck, ShieldCheck, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { Layout } from "../components/Layout";
import { ProductCard } from "../components/ProductCard";
import { NotifyMe } from "../components/NotifyMe";
import { WaitlistCapture } from "../components/WaitlistCapture";
import { useCart } from "../context/CartContext";
import { getProductStatus, BADGE_TONE } from "../lib/productStatus";
import { getConcept, NUTRITION_FAQ, NUTRITION_IMAGE } from "../lib/nutrition";
import api from "../lib/api";

function NutritionConceptPage({ concept, nav }) {
  return (
    <Layout>
      <div className="max-w-[1100px] mx-auto px-5 lg:px-8 py-8">
        <button onClick={() => nav(-1)} className="flex items-center gap-2 text-sm text-eon-muted hover:text-eon-gold mb-6 transition-colors"><ArrowLeft size={16} /> Back to E.O.N Nutrition</button>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          <div className="rounded-3xl overflow-hidden border border-eon-border bg-eon-surface relative">
            <img src={NUTRITION_IMAGE} alt={concept.name} className="w-full h-full object-cover" />
            <span className="absolute top-4 left-4 text-[10px] font-bold tracking-widest uppercase bg-white/90 text-eon-gold rounded-full px-3 py-1">Coming Soon</span>
          </div>
          <div>
            <div className="eon-label">E.O.N Nutrition™ · {concept.category}</div>
            <h1 className="eon-heading text-4xl lg:text-5xl text-eon-text mt-3">{concept.name}</h1>
            <div className="mt-4 inline-block text-xs font-bold tracking-widest uppercase bg-eon-elevated text-eon-gold rounded-full px-4 py-2">Coming Soon — not yet available</div>
            <div className="mt-6">
              <div className="eon-label mb-1">Product Philosophy</div>
              <p className="text-eon-text/80 leading-relaxed">{concept.philosophy}</p>
            </div>
            <div className="mt-6 rounded-2xl border border-eon-border bg-eon-surface p-5">
              <div className="font-display font-bold text-eon-text mb-2">Get launch updates</div>
              <WaitlistCapture subjectType="product_waitlist" subjectId={concept.slug} cta="Join Product Waitlist" />
            </div>
            <p className="mt-4 text-xs text-eon-muted leading-relaxed">
              Ingredients, guaranteed analysis, dosages and any certifications will be published once the formulation is finalized. This page is informational only — no purchase is available.
            </p>
          </div>
        </div>
        <div className="mt-16 max-w-3xl">
          <h2 className="eon-heading text-2xl text-eon-text mb-6">FAQ</h2>
          <div className="space-y-4">
            {NUTRITION_FAQ.map((f) => (
              <div key={f.q} className="eon-card p-5"><div className="font-display font-bold text-eon-text">{f.q}</div><p className="text-sm text-eon-muted mt-1.5 leading-relaxed">{f.a}</p></div>
            ))}
          </div>
        </div>
      </div>
    </Layout>
  );
}

export default function ProductDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { add } = useCart();
  const [p, setP] = useState(null);
  const [related, setRelated] = useState([]);
  const [size, setSize] = useState(null);

  const concept = getConcept(id);

  useEffect(() => {
    if (concept) return;
    api.get(`/products/${id}`).then((r) => {
      setP(r.data);
      setSize(r.data.variants?.[0] || null);
      api.get(`/products?category=${encodeURIComponent(r.data.category)}`).then((rr) => setRelated(rr.data.filter((x) => x.id !== id).slice(0, 4)));
    }).catch(() => {});
  }, [id, concept]);

  if (concept) return <NutritionConceptPage concept={concept} nav={nav} />;
  if (!p) return <Layout><div className="max-w-[1400px] mx-auto px-5 py-32 text-eon-muted">Loading...</div></Layout>;
  const status = getProductStatus(p);

  return (
    <Layout>
      <div className="max-w-[1400px] mx-auto px-5 lg:px-8 py-8">
        <button onClick={() => nav(-1)} className="flex items-center gap-2 text-sm text-eon-muted hover:text-eon-gold mb-6 transition-colors"><ArrowLeft size={16} /> Back</button>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          <div className="relative aspect-square overflow-hidden border border-eon-border bg-eon-elevated">
            <img src={p.image} alt={p.name} className="w-full h-full object-cover" />
            {p.is_eon && <span className="absolute top-4 left-4 eon-label bg-black/60 px-2 py-1">E.O.N Signature</span>}
            {status.showBadge && <span className={`absolute top-4 right-4 eon-label px-2 py-1 ${BADGE_TONE[status.tone]}`}>{status.label}</span>}
          </div>
          <div>
            <div className="eon-label">{p.category} · {p.brand}</div>
            <h1 className="eon-heading text-4xl lg:text-5xl uppercase mt-3">{p.name}</h1>
            <div className="flex items-center gap-2 mt-4 text-sm text-eon-muted"><Star size={14} className="text-eon-gold fill-eon-gold" /> {p.rating} · {p.review_count} reviews</div>
            <div className="font-mono text-3xl text-eon-gold font-medium mt-6">${p.price?.toFixed(2)}</div>
            <p className="mt-6 text-eon-text/80 leading-relaxed">{p.description}</p>
            {p.variants?.length > 0 && (
              <div className="mt-6" data-testid="product-variants">
                <div className="eon-label mb-2">Size / Option</div>
                <div className="flex flex-wrap gap-2">
                  {p.variants.map((v) => (
                    <button key={v} onClick={() => setSize(v)} data-testid={`variant-${v}`}
                      className={`text-sm px-4 py-2 border transition-colors ${size === v ? "bg-eon-gold text-white border-eon-gold" : "border-eon-border text-eon-text/80 hover:border-eon-gold"}`}>{v}</button>
                  ))}
                </div>
              </div>
            )}
            <div className="text-sm text-eon-muted mt-4">{
              status.key === "coming_soon" ? "Launching soon — join the waitlist"
              : status.key === "preorder" ? "Available to pre-order — ships as soon as stock lands"
              : status.key === "sold_out" ? "Sold out — restocking soon"
              : status.key === "reserved" ? "Currently reserved"
              : (p.stock > 0 ? `In stock · ${p.stock} available` : "Out of stock")
            }</div>
            {status.buyable ? (
              <button onClick={() => { add(p, size); toast.success(`Added to cart${size ? ` · ${size}` : ""}${status.key === "preorder" ? " (pre-order)" : ""}`); }} data-testid="product-add-cart"
                className="mt-8 w-full sm:w-auto flex items-center justify-center gap-2 bg-eon-gold text-white text-sm font-bold uppercase tracking-wider px-10 py-4 hover:bg-eon-goldhover transition-colors">
                <ShoppingBag size={18} /> {status.key === "preorder" ? "Pre-Order Now" : "Add to Cart"}
              </button>
            ) : (
              <div data-testid="product-unavailable" className="mt-8 flex flex-col sm:flex-row sm:items-center gap-4">
                <span className="inline-flex items-center justify-center border border-eon-border text-eon-muted text-sm font-bold uppercase tracking-wider px-8 py-4">{status.label}</span>
                <NotifyMe kind="product" itemId={p.id} itemName={p.name}
                  label={status.key === "sold_out" ? "Notify Me When Restocked" : "Notify Me When Available"}
                  triggerTestId="product-notify-btn"
                  className="inline-flex items-center gap-2 bg-eon-gold text-white text-sm font-bold uppercase tracking-wider px-8 py-4 hover:bg-eon-goldhover transition-colors" />
              </div>
            )}
            <div className="grid grid-cols-3 gap-px bg-eon-border border border-eon-border mt-8">
              {[[Truck, "Fast shipping"], [ShieldCheck, "Quality guaranteed"], [RotateCcw, "30-day returns"]].map(([Icon, t]) => (
                <div key={t} className="bg-eon-bg p-4 flex flex-col items-center text-center gap-2"><Icon size={18} className="text-eon-gold" /><span className="text-xs text-eon-muted">{t}</span></div>
              ))}
            </div>
          </div>
        </div>
        {related.length > 0 && (
          <div className="mt-20">
            <h2 className="eon-heading text-3xl uppercase mb-8">More in {p.category}</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">{related.map((r, i) => <ProductCard key={r.id} product={r} index={i} />)}</div>
          </div>
        )}
      </div>
    </Layout>
  );
}
